import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Calendar, MapPin, Award, Users, ArrowRight, CheckCircle2, ArrowUpRight } from 'lucide-react';
import { formatClientCount } from '../utils/stats';

const API = process.env.REACT_APP_BACKEND_URL;

// Map our internal service slugs to real /services/[id] routes when one exists.
// Services without a dedicated page render as plain chips (no link).
const SERVICE_ROUTE_MAP = {
  tonte: 'tonte',
  'taille-haies': 'haies',
  debroussaillage: 'debroussaillage',
  desherbage: 'desherbage',
  vigilance: 'vigilance',
  bricolage: 'bricolage',
  mixte: 'mixte',
  'remise-en-etat': 'remise-en-etat',
  rafraichissement: 'rafraichissement',
  plantation: 'plantation',
  'entretien-general': 'entretien-general',
};

/**
 * SiteActivityShowcase
 *
 * Site-wide "showy" activity dashboard. Same component renders on:
 *  - homepage (no townSlug)
 *  - zone pages (passes townSlug — shows a small contextual badge if town has jobs,
 *    or a friendly "Bientôt votre commune ?" CTA if not)
 *
 * Goals:
 *  - Show off the business (cumulative service breakdown, recurring clients, communes)
 *  - SEO: keyword-rich service chips on every page
 *  - Never display embarrassing "0 jobs" for low-data towns
 */
const SiteActivityShowcase = ({ townSlug, cityName, variant = 'full' }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/public/job-stats`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((json) => {
        if (!cancelled) setStats(json);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!stats || !stats.total_year) return null;

  const {
    total_year,
    month_jobs,
    upcoming_7d,
    recurring_clients,
    total_clients,
    longest_relationship_months,
    business_age_months,
    town_count,
    coverage_town_count,
    by_service = [],
    by_town = [],
  } = stats;

  const communesCount = coverage_town_count ?? town_count;

  // Lookup current town's count (if any)
  const townEntry = townSlug && by_town.find((t) => t.town_slug === townSlug);
  const townCount = townEntry ? townEntry.count : 0;

  // Format months as "X ans" / "X ans Y mois" / "Y mois" with clean rounding
  const formatDuration = (months) => {
    if (!months || months < 1) return { value: '<1', suffix: 'mois' };
    if (months < 12) return { value: months, suffix: 'mois' };
    const years = Math.floor(months / 12);
    const remainder = months % 12;
    if (remainder < 2) {
      return { value: years, suffix: years > 1 ? 'ans' : 'an' };
    }
    return { value: `${years}\u00a0ans\u00a0${remainder}`, suffix: 'mois' };
  };

  const longest = formatDuration(longest_relationship_months);
  const businessAge = formatDuration(business_age_months);

  return (
    <section
      data-testid="site-activity-showcase"
      className="py-12 bg-gradient-to-br from-emerald-50/40 via-white to-amber-50/30"
      aria-labelledby="site-activity-heading"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2 inline-flex items-center gap-1.5">
              <Activity size={14} aria-hidden="true" />
              Mon activité en direct · Le Rosebif depuis {businessAge.value} {businessAge.suffix}
            </p>
            <h2
              id="site-activity-heading"
              className="text-2xl lg:text-3xl font-bold text-gray-900"
            >
              {cityName ? (
                <>Le Rosebif en chiffres — actif partout en Vendée</>
              ) : (
                <>Le Rosebif en chiffres réels</>
              )}
            </h2>
            <p className="text-gray-600 mt-2 text-sm max-w-2xl mx-auto">
              Toutes mes interventions sont enregistrées et anonymisées automatiquement.
              {cityName && (
                <>{' '}Voici un aperçu de mon activité {townCount > 0 ? `dont ${townCount} à ${cityName}` : `couvrant ${cityName} et ${communesCount - 1} autres communes vendéennes`}.</>
              )}
            </p>
          </div>

          {/* 4 hero KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
            <KpiTile
              icon={<Calendar size={20} aria-hidden="true" />}
              value={total_year}
              label="interventions cette année"
              accent="emerald"
            />
            <KpiTile
              icon={<Users size={20} aria-hidden="true" />}
              value={formatClientCount(total_clients ?? recurring_clients)}
              label="clients réguliers"
              accent="emerald"
            />
            <KpiTile
              icon={<MapPin size={20} aria-hidden="true" />}
              value={communesCount}
              label="communes desservies"
              accent="emerald"
            />
            <KpiTile
              icon={<Award size={20} aria-hidden="true" />}
              value={longest.value}
              suffix={` ${longest.suffix}`}
              label="relation client la plus longue"
              accent="amber"
            />
          </div>

          {/* Service breakdown — keyword-rich, SEO-friendly */}
          {by_service.length > 0 && (
            <div className="bg-white rounded-2xl border-2 border-emerald-100 p-5 lg:p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 size={16} className="text-emerald-600" aria-hidden="true" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-emerald-700">
                  Prestations réalisées cette année
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {by_service.map((svc) => {
                  const route = SERVICE_ROUTE_MAP[svc.service];
                  // On a zone page, deep-link to the service+town combo so visitors
                  // land on a page that's both keyword- and location-specific.
                  const href = route && townSlug
                    ? `/services/${route}/${townSlug}`
                    : route ? `/services/${route}` : null;
                  const baseChip = (
                    <>
                      <span className="text-gray-900 font-medium">{svc.label}</span>
                      <span className="inline-flex items-center justify-center min-w-[28px] h-6 bg-emerald-600 text-white font-bold text-xs rounded-full tabular-nums px-2">
                        {svc.count}
                      </span>
                    </>
                  );
                  if (href) {
                    return (
                      <Link
                        key={svc.service}
                        to={href}
                        data-testid={`service-chip-${svc.service}`}
                        className="inline-flex items-center gap-2 bg-gradient-to-br from-emerald-50 to-lime-50 border border-emerald-200 rounded-full pl-3 pr-1.5 py-1 text-sm group hover:border-emerald-500 hover:from-emerald-100 hover:to-lime-100 hover:shadow-sm transition-all"
                      >
                        {baseChip}
                        <ArrowUpRight
                          size={12}
                          className="text-emerald-700 -ml-0.5 mr-0.5 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                          aria-hidden="true"
                        />
                      </Link>
                    );
                  }
                  return (
                    <div
                      key={svc.service}
                      data-testid={`service-chip-${svc.service}`}
                      className="inline-flex items-center gap-2 bg-gradient-to-br from-emerald-50 to-lime-50 border border-emerald-200 rounded-full pl-3 pr-1.5 py-1 text-sm"
                    >
                      {baseChip}
                    </div>
                  );
                })}
              </div>

              <p className="text-xs text-gray-500 mt-4">
                Ces prestations d'entretien ouvrent droit au crédit d'impôt 50 % au titre du Service à la
                Personne déclaré, pour les clients éligibles.
              </p>
            </div>
          )}

          {/* Town-context footer (only when on a town page) */}
          {cityName && (
            <div className="mt-6 text-center">
              {townCount > 0 ? (
                <div
                  className="inline-flex items-center gap-2 bg-white border-2 border-emerald-200 rounded-full px-4 py-2 text-sm text-gray-800 shadow-sm"
                  data-testid="town-context-badge"
                >
                  <span className="relative flex h-2 w-2" aria-hidden="true">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  <strong className="text-emerald-700">{townCount}</strong>
                  intervention{townCount > 1 ? 's' : ''} à
                  <strong>{cityName}</strong>
                  cette année
                </div>
              ) : (
                <div
                  className="inline-flex flex-wrap items-center gap-2 bg-amber-50 border-2 border-amber-200 rounded-2xl px-5 py-3 text-sm text-gray-800"
                  data-testid="town-context-badge"
                >
                  <span className="font-semibold text-amber-900">
                    Secteur d'intervention privilégié
                  </span>
                  <span className="text-gray-600">—</span>
                  <span>Je connais parfaitement votre commune. Contactez-moi pour un devis express et personnalisé.</span>
                  <ArrowRight size={14} className="text-amber-700" aria-hidden="true" />
                </div>
              )}
            </div>
          )}

          {/* Hint about current week */}
          {upcoming_7d < 5 ? (
            <p className="text-sm text-amber-800 font-semibold mt-4 text-center" data-testid="planning-sollicite">
              📍 Planning très sollicité : quelques créneaux encore disponibles cette semaine.
            </p>
          ) : (
            <p className="text-xs text-gray-500 mt-4 text-center">
              {month_jobs > 0 && (
                <>
                  <strong className="text-emerald-700">{month_jobs}</strong> interventions ce mois-ci
                  {' '}· {' '}
                </>
              )}
              <strong className="text-emerald-700">{upcoming_7d}</strong> passages prévus cette
              semaine
              {' '}· données mises à jour automatiquement
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

const KpiTile = ({ icon, value, suffix = '', label, accent = 'emerald' }) => {
  const accentColors = {
    emerald: 'from-emerald-600 to-emerald-700 border-emerald-200',
    amber: 'from-amber-600 to-amber-700 border-amber-200',
  };
  return (
    <div className="bg-white rounded-2xl border-2 border-emerald-100 p-4 text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
      <div
        className={`w-10 h-10 mx-auto mb-2 rounded-full bg-gradient-to-br ${accentColors[accent]} text-white flex items-center justify-center shadow`}
        aria-hidden="true"
      >
        {icon}
      </div>
      <div className="text-3xl font-extrabold text-gray-900 tabular-nums leading-none">
        {value}
        {suffix && <span className="text-base font-bold text-gray-500">{suffix}</span>}
      </div>
      <div className="text-xs text-gray-600 mt-1.5 leading-tight">{label}</div>
    </div>
  );
};

export default SiteActivityShowcase;
